// Mode rail pinned to the right edge of the Explore surface. It replaces
// the old single Context⇄Chat flip-button — which showed the *destination*
// glyph and so always read backwards — with a stack of mode icons whose
// active one is lit: the rail answers "which lens am I in?" rather than
// "where would this take me?".
//
// Scope is deliberately narrow — it ONLY switches the right column's mode
// (Context ⇄ Chat). It does NOT own collapse/expand: that chrome lives on
// the tab strip (the ">" close) and the focus header (the "<" reopen),
// because the rail trying to own collapse fought with the column-expand
// path (expanding the main column implicitly hides the right one, leaving
// a rail toggle showing stale state). The one collapse-adjacent thing it
// does: if you pick a mode while the panel is collapsed, it reopens into
// that mode (via the injected onToggleCollapsed).
//
// It's a quiet panel-surface gutter (not the dark app-nav chrome): buttons
// carry `data-mode-rail-btn`, styled in styles.css as the panel-surface
// counterpart of `[data-origin-mode]` — same flush, borderless shape +
// green inset active bar, but muted-ink on the raised surface.
//
// `collapsed` / `onToggleCollapsed` are injected by SplitPage (it owns the
// detail-column collapse state); `mode` / `onModeChange` / `modes` come
// from the page (ScopedPage owns panelMode).
import HoverTooltip from "./HoverTooltip.jsx";
import { phAttr } from "../lib/analytics/attrs.js";
import { ACTION, ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";

// The right-panel modes an analytics click names; any other mode stays unnamed.
const MODE_ACTIONS = { chat: ACTION.CHAT_PANEL, context: ACTION.CONTEXT_PANEL };

// Mode glyphs, exported so the page can build its `modes` list without
// re-drawing the SVGs. Context = a layout-with-side-panel (distinct from
// the left rail's Studio 2x2 grid, which the old contrast-grid glyph
// collided with); Chat = a bubble.
export const MODE_ICONS = {
  context: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="12" height="10" rx="1" />
      <line x1="9.5" y1="3" x2="9.5" y2="13" />
      <line x1="11" y1="6" x2="12.5" y2="6" />
      <line x1="11" y1="8.2" x2="12.5" y2="8.2" />
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 3h11v7.5H6L3 13v-2.5H2.5z" />
    </svg>
  ),
};

export default function ModeRail({
  modes = [],
  mode,
  onModeChange,
  collapsed = false,
  onToggleCollapsed,
  // True when the detail panel isn't on screen because another column is
  // expanded over it (e.g. the activity surface). Treated like collapsed
  // for the active highlight — the rail won't claim a mode is showing
  // when its panel is hidden.
  panelHidden = false,
}) {
  return (
    <div data-mode-rail>
      <div data-mode-rail-modes>
        {modes.map((m) => {
          // Lit only while the panel is actually on screen and showing
          // this mode. Collapsed OR hidden-behind-an-expand → nothing is
          // "current", so every icon stays dim (a reopen handle) rather
          // than implying a mode is active while the body is off screen.
          const hidden = collapsed || panelHidden;
          const active = m.id === mode && !hidden;
          const label = hidden ? `Open ${m.label}` : m.label;
          return (
            // Tooltip goes to the LEFT — the rail hugs the right edge, so
            // "top"/"right" placements clip off-screen for the top button.
            <HoverTooltip key={m.id} label={label} placement="left">
              <button
                type="button"
                data-mode-rail-btn=""
                data-ph-capture-attribute-action={phAttr(ATTRIBUTE.ACTION, MODE_ACTIONS[m.id])}
                data-active={active ? "true" : "false"}
                aria-pressed={active}
                aria-label={label}
                onClick={() => {
                  onModeChange?.(m.id);
                  // Selecting a mode while collapsed reopens into it.
                  if (collapsed) onToggleCollapsed?.();
                }}
              >
                {m.icon}
              </button>
            </HoverTooltip>
          );
        })}
      </div>
    </div>
  );
}
